import { supabase } from "@/lib/supabase";
import type {
  ActiveEntitlement,
  ActiveUserRole,
  Profile,
  RoleName,
} from "@/types/database";

/**
 * Fetches the caller's own profile row.
 * RLS (migration 003): a user may only SELECT their own profile.
 */
export async function fetchOwnProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

/**
 * Fetches the caller's currently-active (non-revoked) roles.
 * RLS (migration 004): a user may SELECT their own user_roles rows.
 * Revoked assignments (revoked_at IS NOT NULL) are excluded here rather
 * than relying on RLS to filter them, since revocation is a soft-delete,
 * not an access-control boundary — the row is still visible, just inactive.
 */
export async function fetchActiveRoles(userId: string): Promise<ActiveUserRole[]> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role_id, roles(name)")
    .eq("user_id", userId)
    .is("revoked_at", null);

  if (error) throw error;

  return (data ?? []).map((row) => {
    const roles = row.roles as unknown as { name: RoleName } | null;
    return {
      role_id: row.role_id,
      role_name: roles?.name ?? "user",
    };
  });
}

/**
 * Fetches the caller's currently-valid entitlements.
 * Uses entitlement_valid_until (migration 021 — self-maintaining trigger,
 * never NULL) rather than expires_at directly, matching the same fast-path
 * check the database's own fn_user_has_entitlement() function uses.
 * RLS (migration 012): a user may SELECT their own user_entitlements rows.
 */
export async function fetchActiveEntitlements(
  userId: string,
): Promise<ActiveEntitlement[]> {
  const nowIso = new Date().toISOString();

  const { data, error } = await supabase
    .from("user_entitlements")
    .select("entitlement_valid_until, entitlements(key)")
    .eq("user_id", userId)
    .gt("entitlement_valid_until", nowIso);

  if (error) throw error;

  return (data ?? [])
    .map((row) => {
      const entitlement = row.entitlements as unknown as { key: string } | null;
      if (!entitlement) return null;
      return {
        entitlement_key: entitlement.key,
        entitlement_valid_until: row.entitlement_valid_until,
      };
    })
    .filter((row): row is ActiveEntitlement => row !== null);
}

/** True if any of the caller's active roles match one of `allowed`. */
export function hasAnyRole(roles: ActiveUserRole[], allowed: RoleName[]): boolean {
  return roles.some((r) => allowed.includes(r.role_name));
}

/** True if the caller holds an active entitlement with the given key. */
export function hasEntitlement(
  entitlements: ActiveEntitlement[],
  key: string,
): boolean {
  return entitlements.some((e) => e.entitlement_key === key);
}
